/* -----------------Globals--------------- */
import { useCallback, useState } from "react";

/* -----------------UI--------------- */
import { Box, Container, Loader, StickyButtonContainer } from "@memorang/ui";

import { useReviewStore } from "@features/feedback";
import { fetchSession, useSessionStore } from "@features/session";
import { getFilteredSessionItems } from "../helpers/review";
/* -----------------Helpers & Hooks--------------- */
import { useReport } from "../hooks/useReport";

import type { SessionItemTypes } from "@memorang/types";
/* -----------------Types--------------- */
import { router, useLocalSearchParams } from "expo-router";

/* -----------------Child components--------------- */

import { events } from "@constants/tracking";
import { StudySessionProgressBar } from "@features/session";
import { SessionConfigDialog } from "@features/session-config";
import { formatTimeString } from "@helpers/date";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { useDevice } from "@hooks/useDevice";
import { useAuthContext } from "@memorang/applets";
import { isUuid } from "@memorang/helpers";
import { trackCustomEvent } from "analytics";
import { CustomAppbar } from "components/index";
import { useTranslation } from "react-i18next";
import { Appbar } from "react-native-paper";
import { ReportView } from "../components/ReportView";
import type {
	CheckboxStateMap,
	CheckboxType,
} from "../components/dialogs/ReviewAnswersDialog";

const ReportContainer = () => {
	const [showReviewDialog, setShowReviewDialog] = useState(false);
	const trackAppReviewCritieria = useReviewStore(
		(state) => state.trackAppReviewCritieria,
	);
	const [isReviewCriteriaTracked, setIsReviewCriteriaTracked] = useState(false);

	const { isMobile } = useDevice();

	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);

	const toggleSessionConfigDialog = () => {
		setShowSessionConfigDialog((prev) => !prev);
	};

	const { handleSessionConfigNavigation } = useCustomNavigation();
	const [checkboxStateMap, setCheckboxStateMap] = useState<CheckboxStateMap>({
		correct: false,
		incorrect: false,
		skipped: false,
		almostcorrect: false,
		marked: false,
	});

	const handleChangeCheckboxStateMap = useCallback(
		(key: keyof CheckboxStateMap) => {
			setCheckboxStateMap((prev) => {
				return {
					...prev,
					[key]: !prev[key],
				};
			});
		},
		[],
	);
	const handleUpdateIncorrectCheckboxStateMap = useCallback(() => {
		setCheckboxStateMap((prev) => {
			return {
				...prev,
				incorrect: true,
			};
		});
	}, []);

	const { id: sessionIdOrNumericId } = useLocalSearchParams<{
		id: string;
	}>();
	const [fetchingReviewItems, setFetchingReviewItems] = useState(false);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const updateSession = useSessionStore((state) => state.updateSession);
	const updateCurrentBlockIndex = useSessionStore(
		(state) => state.updateCurrentBlockIndex,
	);
	const { reportDetails } = useReport();
	const [selectedItemTypeForReview, setSelectedItemTypeForReview] = useState<
		Extract<SessionItemTypes, "QUESTION"> | undefined
	>();

	const sessionId = isUuid(sessionIdOrNumericId)
		? sessionIdOrNumericId
		: undefined;
	const sessionNumericId = isUuid(sessionIdOrNumericId)
		? undefined
		: Number(sessionIdOrNumericId);

	const { viewerId: userId } = useAuthContext();

	const isProethica = useDemoExamStore(
		(state) => state.currentExam?.id === "proethica",
	);

	const handleReview = async (checkboxStateMap: CheckboxStateMap) => {
		setFetchingReviewItems(true);
		const selectedKeys = Object.keys(checkboxStateMap).filter(
			(key) => checkboxStateMap[key as CheckboxType],
		) as CheckboxType[];
		const { response } = await fetchSession({
			sessionId,
			numericId: sessionNumericId,
			userId: userId!,
		});

		const filteredSessionItems = getFilteredSessionItems(
			response.sessionItems,
			selectedKeys,
			selectedItemTypeForReview,
		);

		if (isSummativeTest) {
			const finalResponse = {
				...response,
				sessionItems: [
					{
						id: "1",
						children: filteredSessionItems,
						itemType: "QUESTION" as const,
						stem: "",
					},
				],
			};
			updateCurrentBlockIndex(0);
			updateSession({
				//@ts-ignore TODO:Fix this once backend is updated with the new schema
				sessionResponse: finalResponse,
				isSummativeTest: true,
				inReviewMode: true,
			});
		} else {
			// updateInReview(true, filteredSessionItems as SessionItem[]);
		}
		setFetchingReviewItems(false);
		router.push({
			pathname: "/(protected)/session/[id]/review",
			params: {
				id: sessionIdOrNumericId,
				index: "1",
			},
		});
		setShowReviewDialog(false);
	};
	const handleContinue = () => {
		resetSessionStore();

		if (router.canGoBack()) {
			router.back();
		}
		router.replace("/home");
	};

	const handleClickActionChip = (type: string) => {
		// trackCustomEvent({
		//   eventName: events.suggestedChipClicked,
		//   type,
		// });
		switch (type) {
			case "review": {
				setSelectedItemTypeForReview("QUESTION");
				setShowReviewDialog(true);
				break;
			}
			case "study": {
				resetSessionStore();
				handleSessionConfigNavigation(toggleSessionConfigDialog);
				break;
			}
			default:
				break;
		}
	};

	const handleReviewButtonClick = () => {
		trackCustomEvent({
			eventName: events.reviewAnswersClicked,
			type: "QUESTION",
		});
		setSelectedItemTypeForReview("QUESTION");
		setShowReviewDialog(true);
	};

	const handleCloseReviewDialog = () => {
		setShowReviewDialog(false);
		setCheckboxStateMap({
			correct: false,
			incorrect: false,
			skipped: false,
			almostcorrect: false,
			marked: false,
		});
	};

	const isSummativeTest = false;

	if (!isReviewCriteriaTracked && reportDetails && !isSummativeTest) {
		trackAppReviewCritieria(reportDetails.score > 60);
		setIsReviewCriteriaTracked(true);
	}

	const breadcrumbs = isSummativeTest
		? [
				{
					label: "Practice Tests",
					href: "/practice-tests",
				},
				{
					label: reportDetails?.examName!,
				},
				...(reportDetails
					? [
							{
								label: `Test #${reportDetails.numericId}`,
							},
						]
					: []),
			]
		: [];

	const creationTime = reportDetails
		? formatTimeString(reportDetails.sessionDate, !isMobile)
		: "";
	const caption = reportDetails
		? `Test #${reportDetails.numericId} ∙ ${creationTime}`
		: "";

	const handleTracking = useCallback(
		(eventName: string, params?: Record<string, string | number>) => {
			trackCustomEvent({
				eventName,
				...(params || {}),
				view: "session_report",
			});
		},
		[],
	);
	const { t } = useTranslation();

	return (
		<>
			{isSummativeTest && (
				<CustomAppbar
					back
					breadcrumbs={breadcrumbs}
					options={{
						headerTitle: reportDetails?.examName,
						subtitle: caption,
						headerRight: () => null,
					}}
				/>
			)}
			<StickyButtonContainer
				buttonText={t("common.continue")}
				maxWidth="sm"
				disabled={!reportDetails}
				onPress={handleContinue}
				showButton={isMobile && !isProethica}
			>
				<Box gap={16} flex={1}>
					{!isSummativeTest && (
						<Appbar.Header
							style={{
								backgroundColor: "transparent",
								height: 80,
							}}
						>
							<Container>
								<StudySessionProgressBar
									sessionType={reportDetails?.reportSessionType}
								/>
							</Container>
						</Appbar.Header>
					)}
					{reportDetails && Object.keys(reportDetails).length > 0 ? (
						<ReportView
							reportDetails={reportDetails}
							fetchingReviewItems={fetchingReviewItems}
							handleContinue={handleContinue}
							handleClickActionChip={handleClickActionChip}
							checkboxStateMap={checkboxStateMap}
							isSummativeTest={isSummativeTest}
							handleTracking={handleTracking}
							handleChangeCheckboxStateMap={handleChangeCheckboxStateMap}
							handleUpdateIncorrectCheckboxStateMap={
								handleUpdateIncorrectCheckboxStateMap
							}
							handleReviewButtonClick={handleReviewButtonClick}
							handleCloseReviewDialog={handleCloseReviewDialog}
							selectedItemTypeForReview={selectedItemTypeForReview}
							handleReview={handleReview}
							showReviewDialog={showReviewDialog}
						/>
					) : (
						<Container
							maxWidth="sm"
							containerStyle={{
								flexGrow: 1,
							}}
						>
							<Loader />
						</Container>
					)}
				</Box>
			</StickyButtonContainer>
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={toggleSessionConfigDialog}
				/>
			) : null}
		</>
	);
};

export default ReportContainer;
